// Browser notification helpers. Best-effort — silently no-ops on browsers
// that don't expose the Notification API, or when the user has denied permission.

let askedThisSession = false;

export function isNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationsSupported()) return 'unsupported';
  return Notification.permission;
}

export async function ensureNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!isNotificationsSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  if (askedThisSession) return Notification.permission;
  askedThisSession = true;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function showNotification(title: string, options: NotificationOptions = {}): void {
  if (!isNotificationsSupported()) return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      ...options,
    });
    // Focus the tab on click.
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    /* some browsers throw on certain options; swallow */
  }
}

// Synthesize a short notification chirp via Web Audio. Avoids shipping an
// audio asset; works without an explicit gesture because the user already
// granted notification permission (which itself required interaction).
let cachedAudioContext: AudioContext | null = null;
export function playNotificationChirp(): void {
  if (typeof window === 'undefined') return;
  try {
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    cachedAudioContext = cachedAudioContext ?? new Ctor();
    const ctx = cachedAudioContext;
    if (ctx.state === 'suspended') void ctx.resume();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.18);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.34);
  } catch {
    /* audio disabled or context creation failed */
  }
}
